import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { LoadingButton } from '../../common/LoadingButton';
import { useCreateLLMProvider, useUpdateLLMProvider } from '../../../services/llm-catalog.service';
import { LLMProvider, LLMProviderFormValues } from '../../../types/admin';
import {
  getConfigString,
  getGatewayModeDescription,
  getProviderGatewayMode,
  parseJsonRecord,
  stringifyJsonRecord,
} from './helpers';
import {
  DEFAULT_GATEWAY_ID,
  DEFAULT_NATIVE_PROVIDER_PATH,
  emptyProvider,
  JsonRecord,
  ProviderGatewayMode,
  providerGatewayModeOptions,
} from './types';

export interface ProviderDialogProps {
  open: boolean;
  provider?: LLMProvider | null;
  onClose: () => void;
}

export const ProviderDialog: React.FC<ProviderDialogProps> = ({ open, provider, onClose }) => {
  const createProvider = useCreateLLMProvider();
  const updateProvider = useUpdateLLMProvider();
  const isSaving = createProvider.isPending || updateProvider.isPending;
  const [values, setValues] = React.useState<LLMProviderFormValues>(emptyProvider);
  const [configError, setConfigError] = React.useState('');

  const parsedConfig = React.useMemo(() => parseJsonRecord(values.config), [values.config]);
  const selectedGatewayMode = !parsedConfig.error
    ? getProviderGatewayMode(parsedConfig.value)
    : ProviderGatewayMode.Auto;
  const nativeProviderEnabled = selectedGatewayMode === ProviderGatewayMode.ProviderNative;

  React.useEffect(() => {
    setValues(
      provider
        ? {
            code: provider.code,
            name: provider.name,
            description: provider.description ?? '',
            apiBaseUrl: provider.apiBaseUrl ?? '',
            credentialKey: provider.credentialKey ?? '',
            config: provider.config ?? '',
            isActive: provider.isActive,
          }
        : emptyProvider
    );
    setConfigError('');
  }, [provider, open]);

  const save = async () => {
    const parsed = parseJsonRecord(values.config);
    if (parsed.error) {
      setConfigError(parsed.error);
      return;
    }

    if (
      getProviderGatewayMode(parsed.value) === ProviderGatewayMode.ProviderNative &&
      !getConfigString(parsed.value, 'customProviderSlug').trim()
    ) {
      setConfigError('Custom provider slug is required for native provider routing');
      return;
    }

    const payload = {
      ...values,
      description: values.description || undefined,
      apiBaseUrl: values.apiBaseUrl || undefined,
      credentialKey: values.credentialKey || undefined,
      config: values.config || undefined,
    };

    if (provider) {
      await updateProvider.mutateAsync({ publicId: provider.publicId, ...payload });
    } else {
      await createProvider.mutateAsync(payload);
    }
    onClose();
  };

  const updateConfig = (updater: (config: JsonRecord) => JsonRecord) => {
    const parsed = parseJsonRecord(values.config);
    if (parsed.error) {
      setConfigError(parsed.error);
      return;
    }

    setConfigError('');
    setValues({
      ...values,
      config: stringifyJsonRecord(updater({ ...parsed.value })),
    });
  };

  const handleGatewayModeChange = (mode: ProviderGatewayMode) => {
    updateConfig((config) => {
      if (mode === ProviderGatewayMode.Auto) {
        delete config.gatewayMode;
        delete config.gatewayId;
        delete config.customProviderSlug;
        delete config.providerPath;
        return config;
      }

      if (mode === ProviderGatewayMode.RestApi) {
        delete config.gatewayId;
        delete config.customProviderSlug;
        delete config.providerPath;
        return {
          ...config,
          gatewayMode: ProviderGatewayMode.RestApi,
        };
      }

      return {
        ...config,
        gatewayMode: ProviderGatewayMode.ProviderNative,
        gatewayId: getConfigString(config, 'gatewayId') || DEFAULT_GATEWAY_ID,
        customProviderSlug: getConfigString(config, 'customProviderSlug'),
        providerPath: getConfigString(config, 'providerPath') || DEFAULT_NATIVE_PROVIDER_PATH,
      };
    });
  };

  const handleNativeConfigChange = (key: string, value: string) => {
    updateConfig((config) => {
      const nextValue = value.trim();
      if (nextValue) {
        config[key] = nextValue;
      } else {
        delete config[key];
      }

      config.gatewayMode = ProviderGatewayMode.ProviderNative;
      return config;
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{provider ? 'Edit Provider' : 'Create Provider'}</DialogTitle>
      <DialogContent>
        <Box className="mt-4 space-y-4">
          <TextField
            fullWidth
            label="Code"
            value={values.code}
            onChange={(e) => setValues({ ...values, code: e.target.value })}
            helperText="openai, anthropic, cloudflare"
          />
          <TextField
            fullWidth
            label="Name"
            value={values.name}
            onChange={(e) => setValues({ ...values, name: e.target.value })}
          />
          <TextField
            fullWidth
            label="API Base URL"
            value={values.apiBaseUrl}
            onChange={(e) => setValues({ ...values, apiBaseUrl: e.target.value })}
          />
          <TextField
            fullWidth
            label="Credential Key"
            value={values.credentialKey}
            onChange={(e) => setValues({ ...values, credentialKey: e.target.value })}
            helperText="Environment or secret binding key"
          />
          <TextField
            fullWidth
            label="Description"
            value={values.description}
            onChange={(e) => setValues({ ...values, description: e.target.value })}
            multiline
            rows={2}
          />
          <Box className="rounded border border-gray-200 p-4 dark:border-slate-700">
            <FormControl fullWidth className="mb-3">
              <InputLabel>Gateway Routing Mode</InputLabel>
              <Select
                label="Gateway Routing Mode"
                value={selectedGatewayMode}
                onChange={(event) =>
                  handleGatewayModeChange(event.target.value as ProviderGatewayMode)
                }
              >
                {providerGatewayModeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" className="mb-3 text-gray-600 dark:text-slate-400">
              {getGatewayModeDescription(selectedGatewayMode)}
            </Typography>
            <Box className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <TextField
                fullWidth
                label="Gateway ID"
                value={getConfigString(parsedConfig.value, 'gatewayId')}
                onChange={(e) => handleNativeConfigChange('gatewayId', e.target.value)}
                disabled={!nativeProviderEnabled}
                helperText="Usually default"
              />
              <TextField
                fullWidth
                label="Custom Provider Slug"
                value={getConfigString(parsedConfig.value, 'customProviderSlug')}
                onChange={(e) => handleNativeConfigChange('customProviderSlug', e.target.value)}
                disabled={!nativeProviderEnabled}
                helperText="local-ollama or custom-local-ollama"
              />
              <TextField
                fullWidth
                label="Provider Path"
                value={getConfigString(parsedConfig.value, 'providerPath')}
                onChange={(e) => handleNativeConfigChange('providerPath', e.target.value)}
                disabled={!nativeProviderEnabled}
                helperText="/v1/chat/completions"
              />
            </Box>
          </Box>
          <TextField
            fullWidth
            label="Config JSON"
            value={values.config}
            onChange={(e) => {
              const nextConfig = e.target.value;
              const parsed = parseJsonRecord(nextConfig);
              setConfigError(parsed.error ?? '');
              setValues({ ...values, config: nextConfig });
            }}
            error={Boolean(configError)}
            helperText={configError || 'Optional provider-level JSON config'}
            multiline
            rows={6}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={save}
          loading={isSaving}
          disabled={!values.code || !values.name}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
